// Types for types management API

export interface TranslationData {
  label: string
  description: string | null
}

export interface TypeValue {
  code: string
  sortOrder: number
  active: boolean
  /**
   * Issue 781 — whether the owning type-table is semantically ordered. Drives
   * CEL ordinal-operator gating (`<`, `<=`, `>`, `>=` are offered only when
   * the source type-table has `ranked = true`).
   */
  ranked: boolean
  translations: Record<string, TranslationData>
  usageCount: number
  settings?: Record<string, unknown> | null
  version: number
  /**
   * Issue 988 — engineer-managed alternative lookup keys for the `[[code]]`
   * marker post-processor. Always present; empty array for type tables that
   * don't carry the `aliases` column.
   */
  aliases: string[]
  /**
   * Issue 988 — optional admin-UI grouping key. Only the `domain_entity_types`
   * admin view clusters rows by this value; other type-table admin views ignore it.
   */
  displayGroup?: string | null
}

export interface TypeTableSummary {
  tableName: string
  displayName: string
  count: number
  activeCount: number
  entityType: boolean
  /**
   * Issue 1013 — whether this lookup is referenced by a business table via FK
   * (derived from `referencingTable != null` on the backend registry). When
   * false, `TypeValue.usageCount` is structurally 0 and the admin UI hides
   * the usage column.
   */
  hasUsageTracking: boolean
}

export interface TypeTableResponse {
  tableName: string
  displayName: string
  values: TypeValue[]
}

export interface LocalesResponse {
  locales: string[]
  defaultLocale: string
}

export interface CreateTypeValueRequest {
  code: string
  sortOrder: number
  translations: Record<string, TranslationData>
}

export interface UpdateTranslationsRequest {
  translations: Record<string, TranslationData>
}

export interface SetActiveRequest {
  active: boolean
}

export interface UpdateSortOrderRequest {
  order: string[]
}

export interface TypeValueSettings {
  icon?: string | null
  color?: string | null
  displayNameTemplate?: string | null
}

export interface UpdateSettingsRequest {
  version: number
  settings: {
    icon?: string | null
    color?: string | null
    displayNameTemplate?: string | null
    menu?: PromotedMenuSettings | null
  }
}

export interface PromotedMenuSettings {
  promote: boolean
  label?: { en?: string; de?: string } | null
  icon?: string | null
  sortOrder?: number | null
}

/* eslint-disable i18next/no-literal-string -- backend enum identifier mirror */
export const PromotedTypeSection = {
  IDENTITY_MANAGEMENT: 'IDENTITY_MANAGEMENT',
  TARGET_SYSTEMS_RESOURCES: 'TARGET_SYSTEMS_RESOURCES',
  ACCESS_GOVERNANCE: 'ACCESS_GOVERNANCE',
} as const
/* eslint-enable i18next/no-literal-string */
export type PromotedTypeSection = typeof PromotedTypeSection[keyof typeof PromotedTypeSection]

export interface PromotedTypeView {
  entityTable: string
  parentSection: PromotedTypeSection
  code: string
  label: Record<string, string>
  icon?: string | null
  sortOrder: number
}

/* eslint-disable i18next/no-literal-string -- field name constants */
export const CreateTypeValueRequestFields = {
    code: 'code',
    sortOrder: 'sortOrder',
    translations: 'translations',
} as const
/* eslint-enable i18next/no-literal-string */
